'use client';

import { memo, ReactNode } from 'react';

import { ChevronDown } from 'lucide-react';
import { useTranslations } from 'next-intl';

import type { TrailIconRenderer } from '@/components/assistant-ui/thread/rotating-trail-icon';
import type { TimelineRow } from '@/components/assistant-ui/thread/trail-records';
import { TrailStepStatus } from '@/components/assistant-ui/thread/trail-step-icon';
import { cn } from '@/lib/utils';

export type TrailRowProps = {
  row: TimelineRow;
  expanded: boolean;
  isCurrent: boolean;
  showChevron: boolean;
  renderInProgressIcon: TrailIconRenderer;
  renderTrailing?: (row: TimelineRow) => ReactNode | undefined;
  /**
   * When true, drops the `motion-safe:animate-trail-row-in` keyframe on
   * the row's `<li>`. Used by the panel-mode branch of `CollapsingTrail`,
   * which lives in a different DOM hierarchy than the live flat trail —
   * the swap forces a full remount and the keyframe would otherwise replay
   * for every row at once, producing a jarring flicker before the panel
   * collapses.
   */
  skipSlideIn?: boolean;
};

// High-churn fields first so a real change short-circuits before the stable
// id/kind/callback comparisons during streaming.
const ROW_COMPARE_KEYS = [
  'status',
  'waitsFor',
  'liveAnchor',
  'isInFlight',
  'durationMs',
  'label',
  'id',
  'kind',
] as const satisfies readonly (keyof TimelineRow)[];

const PROPS_COMPARE_KEYS = [
  'isCurrent',
  'showChevron',
  'skipSlideIn',
  'renderInProgressIcon',
  'renderTrailing',
] as const satisfies readonly (keyof TrailRowProps)[];

// Its own component so `useTranslations` — a context read plus a retained translator per caller —
// runs only for the rare failed, cancelled or waiting row, not for every row on every streaming tick.
function StepAnnouncement({ messageKey }: { messageKey: string }) {
  const t = useTranslations();
  return <span className="sr-only">{t(messageKey)}</span>;
}

// The status icon is aria-hidden, so without this a screen reader hears a failed or cancelled step
// as having succeeded, or misses that it is the guest's turn. Mounting inside the thread's polite log
// announces it once, when the row enters that state.
function announcementKey(row: TimelineRow): string | undefined {
  if (row.status === 'failed') return 'activityTrail.stepFailed';
  if (row.status === 'cancelled') return 'activityTrail.stepCancelled';
  if (row.status === 'awaiting-approval') {
    return `activityTrail.actionNeeded.${row.waitsFor ?? 'confirm'}`;
  }
  return undefined;
}

export const TrailRow = memo(
  function TrailRow({
    row,
    expanded,
    isCurrent,
    showChevron,
    renderInProgressIcon,
    renderTrailing,
    skipSlideIn,
  }: TrailRowProps) {
    const trailingNode = renderTrailing?.(row);
    const announcement = announcementKey(row);
    return (
      <li
        aria-current={isCurrent ? 'step' : undefined}
        className={cn(
          'flex items-start gap-3 rounded-md px-3 py-2 transition-colors',
          !skipSlideIn && 'motion-safe:animate-trail-row-in',
        )}
      >
        <TrailStepStatus
          status={row.status}
          customInProgressIcon={renderInProgressIcon({ stepId: row.id })}
          iconKey={row.id}
          waitsFor={row.waitsFor}
          label={row.label}
          announcement={announcement && <StepAnnouncement messageKey={announcement} />}
        />
        {trailingNode && (
          <span className="ms-2 flex shrink-0 items-center pt-0.5">{trailingNode}</span>
        )}
        {showChevron && (
          <span className="ms-2 flex shrink-0 items-center pt-0.5">
            <ChevronDown
              aria-hidden="true"
              className={cn(
                'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ease-out motion-reduce:transition-none',
                expanded && 'rotate-180',
              )}
            />
          </span>
        )}
      </li>
    );
  },
  // Render callbacks are useMemo'd in the parent, so reference equality on
  // function fields is sufficient. `expanded` is only read for the chevron
  // rotation, gated by `showChevron` — without that guard, every panel row
  // would re-render on each toggle just to compute the same DOM.
  (prev, next) =>
    ROW_COMPARE_KEYS.every(k => prev.row[k] === next.row[k]) &&
    PROPS_COMPARE_KEYS.every(k => prev[k] === next[k]) &&
    (!next.showChevron || prev.expanded === next.expanded),
);
